import React from 'react';
import {
  formatMetric,
  getProgressPercent,
  getResultMetric,
  getStatusTone,
  hasNumber
} from './nationalizationViewModel';

export default function NationalizationOverviewCard({ overview, programLabel, language, t, children }) {
  const current = overview?.current || {};
  const metric = getResultMetric(current);
  const progress = getProgressPercent(metric);
  const explicitStatusTone = getStatusTone(current?.status_code);
  // Confidence and progress are separate ideas. A beta/provisional result still
  // shows red or green against the calculated target, while the warning below the
  // card explains that the legal catalogue or evidence is incomplete. This only
  // controls presentation; it never changes the backend compliance status.
  const statusTone = hasNumber(metric.current) && hasNumber(metric.required)
    ? Number(metric.current) >= Number(metric.required) ? 'success' : 'danger'
    : explicitStatusTone;
  const statusColor = statusTone === 'success' ? '#40EB6E' : statusTone === 'danger' ? '#EB4040' : '#568ded';
  const currentValue = formatMetric(metric.current, metric.suffix, 1, language);
  const requiredValue = formatMetric(metric.required, metric.suffix, 1, language);
  const hasRequirement = metric.required !== null && metric.required !== undefined && metric.required !== '';
  // The hero wording follows the legal result type. Calling an absolute target
  // or an official classification a "rate" would misstate the government rule.
  const heroLabel = metric.resultType === 'count'
    ? t('page.overallCount', 'Qualifying {{program}} employee count', { program: programLabel })
    : metric.resultType === 'band'
      ? t('page.overallBand', 'Official {{program}} classification', { program: programLabel })
      : metric.resultType === 'percentage'
        ? t('page.overallRate', 'Overall {{program}} Rate', { program: programLabel })
        : t('page.overallResult', '{{program}} result', { program: programLabel });
  const requirementLabel = metric.resultType === 'band'
    ? t('page.requiredBand', 'Required classification: {{value}}', { value: requiredValue })
    : metric.resultType === 'count'
      ? t('page.requiredCountValue', 'Required: {{value}} qualifying employees', { value: requiredValue })
      : t('page.requiredValue', 'Required: at least {{value}}', { value: requiredValue });

  return (
    <div className="nationalization-card nationalization-overview-card">
      <div className={`nationalization-overall-row ${metric.resultType === 'band' ? 'is-band' : ''}`}>
        <div className={`nationalization-rate-copy ${metric.resultType === 'band' ? 'is-band' : ''}`}>
          <p>{heroLabel}</p>
          <strong>{currentValue}</strong>
          <span style={{ color: statusColor }}>
            {hasRequirement
              ? requirementLabel
              : t('resultStates.targetUnavailable', 'No verified numeric target is available yet')}
          </span>
          {hasNumber(current?.basic_rate_pct) && metric.resultType === 'percentage' && (
            <small className="nationalization-basic-rate">
              {t('page.basicWorkforceRate', 'Basic workforce rate: {{value}}%', {
                value: formatMetric(current.basic_rate_pct, '', 1, language)
              })}
            </small>
          )}
          {current?.official_band && metric.resultType === 'percentage' && (
            <small className={`nationalization-calculated-band nationalization-tone-${statusTone}`}>
              {t('page.calculatedNitaqatBand', 'Calculated Nitaqat band: {{band}}', {
                band: t(`nitaqatBands.${current.official_band}`, current.official_band)
              })}
            </small>
          )}
        </div>

        {metric.resultType !== 'band' && (
          <div
            className="nationalization-progress"
            style={{
              '--nationalization-color': statusColor,
              '--progress-position': `${progress ?? 0}%`
            }}
          >
            <div className="nationalization-progress-label-row">
              <span className="nationalization-progress-current">{currentValue}</span>
            </div>
            <div
              className={`nationalization-progress-track ${progress === null ? 'is-unknown' : ''}`}
              role="progressbar"
              aria-label={t('page.progressLabel', '{{program}} progress', { program: programLabel })}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={progress === null ? undefined : Math.round(progress)}
            >
              {progress !== null && <div style={{ width: `${progress}%` }} />}
            </div>
            <div className="nationalization-progress-label-row nationalization-progress-required-row">
              <span>{hasRequirement ? requiredValue : '—'}</span>
            </div>
          </div>
        )}
      </div>

      <div className="nationalization-separator" />

      {children}
    </div>
  );
}
